import React, { useState } from 'react';
import { ShoppingCart, Share2, X } from 'lucide-react';
import { books, faqData, allTopics } from '../../data/books';
import { RedeemSection } from './RedeemSection';
import { BASE_URL } from '../../data/constants';

// SVGs inline reutilizados no mini-modal
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);
const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.737-8.835L1.254 2.25H8.08l4.261 5.635 5.903-5.635zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// Ícone de robô para o badge de IA
const RobotIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5 flex-shrink-0">
    <rect x="3" y="8" width="18" height="11" rx="2" />
    <path d="M12 3v5M9 3h6" strokeLinecap="round" />
    <circle cx="8.5" cy="13.5" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="15.5" cy="13.5" r="1.5" fill="currentColor" stroke="none" />
    <path d="M9 17h6" strokeLinecap="round" />
  </svg>
);

// Mini-modal de compartilhamento do livro
const BookShareModal = ({ side, onClose }) => {
  const [copied, setCopied] = useState(false);
  const book = books[side];
  const shareText = `${book.emoji} Li "${book.title}" e recomendo! ${book.subtitle}. Junte-se a nós e fortaleça a ${side.toUpperCase()} no EbookArena!`;
  const shareUrl = `${BASE_URL}?side=${side}`;
  const full = `${shareText} ${shareUrl}`;

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(full); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-2xl max-w-sm w-full p-5 shadow-2xl border-2 ${book.borderColor}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-14 ${book.color} rounded-lg flex items-center justify-center text-xl text-white shadow flex-shrink-0`}>
            {book.emoji}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 text-sm truncate">{book.title}</p>
            <p className={`text-xs truncate ${book.textColor}`}>{book.subtitle}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 flex-shrink-0" aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        {/* Preview */}
        <p className="text-xs text-gray-500 italic bg-gray-50 rounded-lg p-3 border mb-4 leading-relaxed">
          "{shareText}"
        </p>

        {/* Botões */}
        <div className="flex flex-wrap gap-2">
          <a href={`https://wa.me/?text=${encodeURIComponent(full)}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 bg-[#25D366] text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-all">
            <WhatsAppIcon /> WhatsApp
          </a>
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 bg-black text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-all">
            <TwitterIcon /> Twitter / X
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 bg-[#1877F2] text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-all">
            <FacebookIcon /> Facebook
          </a>
          <button onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-300 transition-all">
            {copied ? '✅ Copiado!' : '🔗 Copiar link'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const BooksSection = ({ sales, handlePurchase }) => {
  const [shareModal, setShareModal] = useState(null); // null | 'esquerda' | 'direita'

  return (
    <>
      {/* Mini-modal de compartilhamento do livro */}
      {shareModal && <BookShareModal side={shareModal} onClose={() => setShareModal(null)} />}

      {/* LIVROS */}
      <section className="py-12" aria-labelledby="livros-heading">
        <div className="text-center mb-12">
          <h2 id="livros-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            ⚔️ Escolha seu lado e entre na batalha
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Ebooks curtos, diretos e atualizados. Feitos com IA de ponta + curadoria humana.
          </p>

          <div style={{
            background: '#fffbea',
            border: '1.5px solid #f0c040',
            borderLeft: '5px solid #f0a500',
            borderRadius: 10,
            maxWidth: 820,
            margin: '20px auto 0 auto',
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
          }}>
            <span style={{ fontSize: '1.5rem', lineHeight: 1.3, flexShrink: 0 }}>📌</span>
            <p style={{ margin: 0, fontSize: '0.95rem', color: '#5a4200', lineHeight: 1.6 }}>
              <strong>Nota importante:</strong> Neste livro, os pensadores e figuras citadas receberam{' '}
              <strong>nomes fictícios</strong>. Essa escolha é intencional: queremos que você avalie
              os argumentos pelo <em>mérito das ideias</em>, não pela reputação de quem as defende.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:gap-8 relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 transform -translate-x-1/2 z-10" aria-hidden="true"></div>
          
          {Object.entries(books).map(([side, book]) => {
            // AGORA as variáveis estão DENTRO do loop, onde 'side' existe
            const isTopSeller = sales[side] > sales[side === 'esquerda' ? 'direita' : 'esquerda'];
            const isEmpate = sales.esquerda === sales.direita;
            
            return (
              <article key={side} className="relative" itemScope itemType="https://schema.org/Book">
                <div className={`bg-white rounded-xl lg:rounded-3xl shadow-xl overflow-hidden border-2 lg:border-4 ${book.borderColor} transform hover:scale-105 transition-all duration-300 h-full relative`}>

                  {/* Badge "MAIS VENDIDO" aparece apenas no livro líder */}
                  {isTopSeller && !isEmpate && (
                    <div className={`absolute top-4 right-4 ${book.color} text-white px-3 py-1 rounded-full text-xs font-bold z-10 shadow-lg`}>
                      🔥 MAIS VENDIDO
                    </div>
                  )}

                  <div className="p-4 sm:p-6 lg:p-8 h-full flex flex-col">
                    {/* Capa do livro */}
                    <div className="text-center mb-4 lg:mb-6 flex-shrink-0">
                      <div className={`relative mx-auto w-24 h-32 sm:w-32 sm:h-40 lg:w-40 lg:h-52 rounded-lg shadow-lg border-2 ${book.borderColor} mb-4 overflow-hidden`}>
                        {book.thumbnail ? (
                          <img
                            src={book.thumbnail}
                            alt={`Capa do ${book.title}`}
                            className="w-full h-full object-cover"
                            itemProp="image"
                          />
                        ) : (
                          <div className={`w-full h-full ${book.color} flex flex-col items-center justify-center text-white relative`}>
                            <div className="text-2xl lg:text-4xl mb-2" role="img" aria-label={`Capa do ${book.title}`}>{book.emoji}</div>
                            <div className="text-xs lg:text-sm font-bold text-center px-2 leading-tight" itemProp="name">
                              {book.title}
                            </div>
                            <div className="absolute bottom-2 left-0 right-0 text-xs opacity-75" itemProp="author">
                              {book.author}
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
                          </div>
                        )}
                      </div>
                      
                      {/* Barra de calor — intensidade proporcional às vendas */}
                      {(() => {
                        const maxSales = Math.max(sales.esquerda || 0, sales.direita || 0);
                        const thisSales = sales[side] || 0;
                        function visLevel(s, m) {
                          const MIN_FLOOR = 0.52, MAX_CAP = 0.94;
                          const logRel = m === 0 ? 0 : Math.log1p(s) / Math.log1p(m);
                          return MIN_FLOOR + logRel * (MAX_CAP - MIN_FLOOR);
                        }
                        const level = visLevel(thisSales, maxSales);
                        const pct = Math.round(level * 100);
                        const isEsq = side === 'esquerda';
                        // interpola cor: fraco → intenso
                        const fillColor = isEsq
                          ? `color-mix(in srgb, #dc2626 ${pct}%, #fecaca)`
                          : `color-mix(in srgb, #16a34a ${pct}%, #bbf7d0)`;
                        return (
                          <div className="mb-2 px-0.5" itemScope itemType="https://schema.org/AggregateRating">
                            <meta itemProp="ratingValue" content={book.rating} />
                            <meta itemProp="bestRating" content="5" />
                            <meta itemProp="ratingCount" content={thisSales + 50} />
                            {/* track */}
                            <div className="w-full bg-gray-100 rounded-full overflow-hidden" style={{ height: 10 }}>
                              <div
                                style={{
                                  width: `${pct}%`,
                                  height: '100%',
                                  background: fillColor,
                                  borderRadius: 999,
                                  transition: 'width 0.6s ease',
                                }}
                              />
                            </div>
                            <p className="text-right text-gray-400 mt-0.5" style={{ fontSize: 10 }}>
                              {thisSales} {thisSales === 1 ? 'venda' : 'vendas'}
                            </p>
                          </div>
                        );
                      })()}
                      
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-1 leading-tight" itemProp="name">{book.title}</h3>
                      <p className={`text-sm sm:text-base lg:text-lg font-semibold ${book.textColor} mb-2`} itemProp="alternateName">{book.subtitle}</p>

                      {/* Badge de IA */}
                      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold ${book.bgLight} ${book.textColor} border ${book.borderColor} mb-1`}>
                        <RobotIcon />
                        <span itemProp="author">{book.aiLabel}</span>
                      </div>
                    </div>

                    {/* Descrição e features */}
                    <div className="flex-grow mb-4">
                      <p className="text-xs sm:text-sm lg:text-base text-gray-700 mb-4 leading-relaxed" itemProp="description">{book.description}</p>
                      
                      {/* Features principais */}
                      <div className="space-y-2 mb-4">
                        {book.features.slice(0, 3).map((feature, index) => (
                          <div key={index} className="flex items-center text-xs lg:text-sm text-gray-600">
                            <span className={`w-1.5 h-1.5 ${book.color} rounded-full mr-2 flex-shrink-0`} aria-hidden="true"></span>
                            {feature}
                          </div>
                        ))}
                      </div>
                      
                    </div>

                    {/* Preço e CTA com Schema.org */}
                    <div className="mt-auto" itemScope itemType="https://schema.org/Offer">
                      <meta itemProp="availability" content="https://schema.org/InStock" />
                      <meta itemProp="priceCurrency" content="BRL" />
                      <meta itemProp="seller" content="EbookArena" />
                      
                      <div className="text-center mb-4">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <span className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900" itemProp="price" content={book.priceValue}>{book.price}</span>
                          <span className="text-sm text-gray-500 line-through">{book.originalPrice}</span>
                          <span className={`${book.color} text-white px-2 py-1 rounded text-xs font-bold`}>
                            50% OFF
                          </span>
                        </div>
                        <p className="text-xs text-gray-500"><span itemProp="numberOfPages">{book.pages}</span> • Download imediato</p>
                      </div>

                      {/* CTA + Compartilhar */}
                      <div className="flex flex-col gap-2 lg:flex-row">
                        <button
                          onClick={() => handlePurchase(side)}
                          className={`w-full py-3 lg:py-4 px-3 lg:px-6 rounded-lg lg:rounded-xl text-white font-bold text-xs sm:text-sm lg:text-base transition-all ${book.color} ${book.hoverColor} transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-1.5`}
                          aria-label={`Comprar ebook ${book.title}`}
                        >
                          <ShoppingCart size={14} className="lg:w-4 lg:h-4 flex-shrink-0" aria-hidden="true" />
                          <span className="leading-tight">{book.ctaButton}</span>
                        </button>
                        <button
                          onClick={() => setShareModal(side)}
                          className={`w-full lg:w-11 py-2 lg:py-0 rounded-lg lg:rounded-xl border-2 ${book.borderColor} ${book.bgLight} ${book.textColor} hover:opacity-80 transition-all flex items-center justify-center gap-1.5 lg:gap-0`}
                          aria-label={`Compartilhar ${book.title}`}
                          title="Compartilhar este ebook"
                        >
                          <Share2 size={14} />
                          <span className="text-xs font-semibold lg:hidden">Compartilhar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Como esses livros foram feitos */}
        <div className="mt-8 max-w-3xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4">
          <p className="flex items-center gap-2 font-semibold text-gray-800 text-sm sm:text-base mb-2">
            <RobotIcon /> Como esses livros foram feitos
          </p>
          <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
            O mesmo prompt foi aplicado a duas IAs. Cada uma recebeu os mesmos <strong>18 temas</strong> e gerou sua visão política sem intervenção humana. Cada capítulo foi processado individualmente — as IAs não tiveram acesso ao contexto dos outros capítulos.
          </p>
          <p className="mt-3 text-xs text-gray-400 leading-relaxed">
            <span className="font-medium">Temas:</span> {allTopics.join(' · ')}
          </p>
        </div>
      </section>

      {/* Resgatar Ebook */}
      <RedeemSection />

      {/* FAQ Section para Schema.org */}
      <section className="py-12" aria-labelledby="faq-heading" itemScope itemType="https://schema.org/FAQPage">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          <h2 id="faq-heading" className="text-3xl font-bold text-center mb-8 text-gray-900">
            ❓ Perguntas Frequentes
          </h2>
          <div className="max-w-4xl mx-auto space-y-6">
            {faqData.map((faq, index) => (
              <details key={index} className="border rounded-lg p-4" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <summary className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors" itemProp="name">
                  {faq.question}
                </summary>
                <div className="mt-3 text-gray-700" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p itemProp="text">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer com elementos semânticos */}
      <footer className="bg-gray-800 text-white py-12 mt-16" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">📖 Sobre os Ebooks</h3>
              <p className="text-gray-300">Análises políticas profundas e imparciais para formar sua própria opinião</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">📞 Contato</h3>
              <p className="text-gray-300">
                <a href="mailto:contato@ebookarena.com.br" className="hover:text-white transition-colors">
                  contato@ebookarena.com.br
                </a>
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">🔒 Segurança</h3>
              <p className="text-gray-300">Pagamentos 100% seguros via PIX</p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-700 text-gray-400">
            <p>&copy; {new Date().getFullYear()} EbookArena. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </>
  );
};